import { useState, useRef, FormEvent } from "react";

const API_URL = "https://api.scopeshield.app";
const APP_LOGIN_URL = "https://app.scopeshield.app/login";

// ─── Logo ────────────────────────────────────────────────────────────────────

function ShieldIcon({ size = 28, color = "white" }: { size?: number; color?: string }) {
    const h = Math.round(size * 1.15);
    return (
        <svg
            width={size}
            height={h}
            viewBox="0 0 28 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            {/* Outer shield */}
            <path
                d="M14 1L2 5.5V16C2 23.5 8 29.5 14 31C20 29.5 26 23.5 26 16V5.5L14 1Z"
                stroke={color}
                strokeWidth="1.6"
                strokeLinejoin="round"
                fill="none"
            />
            {/* Center divider */}
            <line
                x1="14"
                y1="1.5"
                x2="14"
                y2="30.5"
                stroke={color}
                strokeWidth="1.6"
            />
            {/* Right-side ascending bars */}
            <line x1="18" y1="24" x2="18" y2="21" stroke={color} strokeWidth="2" strokeLinecap="round" />
            <line x1="21" y1="24" x2="21" y2="17" stroke={color} strokeWidth="2" strokeLinecap="round" />
            <line x1="24" y1="24" x2="24" y2="12" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

function Logo() {
    return (
        <div className="flex items-center gap-2.5">
            <ShieldIcon size={26} />
            <span className="text-white font-semibold text-lg tracking-tight">
                ScopeShield
            </span>
        </div>
    );
}

// ─── Nav ─────────────────────────────────────────────────────────────────────

function Nav({ onRequestPilot }: { onRequestPilot: () => void }) {
    return (
        <header className="fixed top-0 inset-x-0 z-50 border-b border-slate-900 bg-[#020617]/90 backdrop-blur-sm">
            <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                <Logo />
                <nav className="flex items-center gap-6">
                    <button
                        onClick={onRequestPilot}
                        className="text-sm text-slate-400 hover:text-white transition-colors"
                    >
                        Request an Audit
                    </button>
                    <a
                        href={APP_LOGIN_URL}
                        className="text-sm text-slate-400 hover:text-white transition-colors"
                    >
                        Sign In
                    </a>
                </nav>
            </div>
        </header>
    );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function Hero({ onRequestPilot }: { onRequestPilot: () => void }) {
    return (
        <section className="pt-40 pb-28 px-6">
            <div className="max-w-4xl mx-auto text-center">
                <p className="text-amber-400 text-xs font-semibold uppercase tracking-[0.2em] mb-6">
                    AI-powered scope creep detection for AEC
                </p>
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.08] tracking-tight">
                    Raise Your Shield.<br />
                    Protect Your Margin.
                </h1>
                <p className="mt-7 text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
                    Civil engineering firms lose 5–15% of project margin to scope
                    creep that hides in email, meeting notes, and other project
                    documents.<br />
                    ScopeShield surfaces it before it hits the P&amp;L.
                </p>
                <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                    <button
                        onClick={onRequestPilot}
                        className="px-8 py-3.5 rounded-lg bg-amber-400 text-slate-900 font-semibold text-sm hover:bg-amber-300 transition-colors"
                    >
                        Request an Audit →
                    </button>
                    <a
                        href="/demo"
                        className="px-8 py-3.5 rounded-lg border border-slate-700 text-slate-300 text-sm hover:border-slate-500 hover:text-white transition-colors"
                    >
                        Interactive Demo
                    </a>
                    <a
                        href={APP_LOGIN_URL}
                        className="px-8 py-3.5 rounded-lg border border-slate-700 text-slate-300 text-sm hover:border-slate-500 hover:text-white transition-colors"
                    >
                        Sign In
                    </a>
                </div>
            </div>
        </section>
    );
}

// ─── Pain ─────────────────────────────────────────────────────────────────────

function Pain() {
    return (
        <section className="border-t border-slate-900 py-24 px-6">
            <div className="max-w-5xl mx-auto">
                <p className="text-slate-500 text-xs font-semibold uppercase tracking-[0.2em] mb-14">
                    The problem
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                    <div>
                        <p className="text-5xl font-bold text-white tabular-nums">8+</p>
                        <p className="mt-3 text-slate-400 leading-relaxed">
                            Projects your PMs manage simultaneously — too many to
                            manually compare every email against the baseline scope.
                        </p>
                    </div>
                    <div>
                        <p className="text-5xl font-bold text-white tabular-nums">
                            5–15%
                        </p>
                        <p className="mt-3 text-slate-400 leading-relaxed">
                            Of project margin lost to undetected scope creep
                            industry-wide. Most firms never recover it.
                        </p>
                    </div>
                    <div>
                        <p className="text-5xl font-bold text-white tabular-nums">$0</p>
                        <p className="mt-3 text-slate-400 leading-relaxed">
                            Recovered when the work is already done. Late discovery
                            makes billing politically impossible.
                        </p>
                    </div>
                </div>

                <p className="mt-16 border-l-2 border-amber-400 pl-6 text-xl text-slate-400 leading-relaxed">
                    Most firms know scope creep is happening. Few can prove it in
                    time to bill for it.<br />
                    ScopeShield changes that.
                </p>
            </div>
        </section>
    );
}

// ─── How It Works ─────────────────────────────────────────────────────────────

function HowItWorks() {
    const steps = [
        {
            title: "Upload your project documents",
            body: "Upload your baseline scope alongside incoming project documents — meeting notes, emails, RFIs. ScopeShield ingests the full project record.",
        },
        {
            title: "Vigilo Engine analyzes",
            body: "Our AI compares every incoming document against your contracted baseline, flagging items that fall outside scope — with severity and likelihood ratings.",
        },
        {
            title: "Act on documented findings",
            body: "Each flag links to the exact source text. Additional Service Request drafts are generated and ready for your review.",
        },
    ];

    return (
        <section className="border-t border-slate-900 py-24 px-6">
            <div className="max-w-5xl mx-auto">
                <p className="text-slate-500 text-xs font-semibold uppercase tracking-[0.2em] mb-14">
                    How it works
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                    {steps.map((step) => (
                        <div key={step.title}>
                            <div className="mb-4">
                                <ShieldIcon size={20} color="#fbbf24" />
                            </div>
                            <h3 className="text-white font-semibold text-lg leading-snug mb-3">
                                {step.title}
                            </h3>
                            <p className="text-slate-400 leading-relaxed text-sm">
                                {step.body}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ─── Differentiators ──────────────────────────────────────────────────────────

function Differentiators() {
    const cards = [
        {
            title: "Purpose-built for AEC",
            body: "Not a generic AI tool adapted for construction. The Vigilo Engine and ASR drafting are designed for how civil firms actually work — built around the document types, project structures, and service categories that define AEC work.",
        },
        {
            title: "Evidence-backed flags",
            body: "Every flag links to the exact source text snippet. No black-box output. You verify each finding in seconds and stand behind it in a client conversation.",
        },
        {
            title: "ASR drafts, ready to refine",
            body: "When scope creep is flagged, ScopeShield drafts the Additional Service Request for you — capturing the issue clearly so your PM can review, adjust the fee, and send.",
        },
        {
            title: "Privacy-first by design",
            body: "PII is automatically scrubbed before any data reaches external AI services. Row-level security. Organization-scoped access. Full audit trail.",
        },
    ];

    return (
        <section className="border-t border-slate-900 py-24 px-6">
            <div className="max-w-5xl mx-auto">
                <p className="text-slate-500 text-xs font-semibold uppercase tracking-[0.2em] mb-14">
                    Why ScopeShield
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {cards.map((card) => (
                        <div
                            key={card.title}
                            className="rounded-xl border border-slate-800 bg-slate-900/40 p-6"
                        >
                            <h3 className="text-white font-semibold mb-3">
                                {card.title}
                            </h3>
                            <p className="text-slate-400 text-sm leading-relaxed">
                                {card.body}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ─── Origin ───────────────────────────────────────────────────────────────────

function Origin() {
    return (
        <section className="border-t border-slate-900 py-24 px-6">
            <div className="max-w-5xl mx-auto text-center">
                <p className="text-slate-500 text-xs font-semibold uppercase tracking-[0.2em] mb-12">
                    Built from the inside.
                </p>
                <div className="max-w-2xl mx-auto">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-6">
                        Designed by a civil PM who lived it.
                    </h2>
                    <p className="text-slate-400 leading-relaxed mb-4">
                        ScopeShield was developed by a civil project manager who has
                        managed site development portfolios and understands how scope
                        exposure accumulates across routine communication.
                    </p>
                    <p className="text-slate-400 leading-relaxed">
                        It was built to introduce structured comparison and documented
                        visibility where manual oversight breaks down.
                    </p>
                </div>
            </div>
        </section>
    );
}

// ─── Pilot Form ───────────────────────────────────────────────────────────────

type FormState = "idle" | "submitting" | "success" | "error";

function PilotForm({ formRef }: { formRef: React.RefObject<HTMLElement> }) {
    const [name, setName] = useState("");
    const [company, setCompany] = useState("");
    const [email, setEmail] = useState("");
    const [state, setState] = useState<FormState>("idle");
    const [errorMsg, setErrorMsg] = useState("");

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setState("submitting");
        setErrorMsg("");

        try {
            const res = await fetch(`${API_URL}/api/pilot-request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, company, email }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error((data as { error?: string }).error ?? "Request failed");
            }

            setState("success");
        } catch (err) {
            setErrorMsg(
                err instanceof Error ? err.message : "Something went wrong. Please try again."
            );
            setState("error");
        }
    };

    return (
        <section
            ref={formRef}
            className="border-t border-slate-900 py-24 px-6"
            id="pilot"
        >
            <div className="max-w-lg mx-auto">
                <p className="text-slate-500 text-xs font-semibold uppercase tracking-[0.2em] mb-8">
                    The Concierge Margin Audit
                </p>
                <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-4">
                    $5,000. 14 days.<br />One conclusive answer.
                </h2>
                <p className="text-slate-400 leading-relaxed mb-10">
                    Scope creep is expensive when it goes unnoticed.
                    Upload your scope documents and project communications, run the analysis,
                    and ScopeShield surfaces where work may be drifting beyond the original scope
                    — with clear evidence from your project record.
                </p>

                {state === "success" ? (
                    <div className="rounded-xl border border-amber-800 bg-amber-950/40 p-6">
                        <p className="text-amber-400 font-semibold mb-1">
                            Request received.
                        </p>
                        <p className="text-slate-400 text-sm">
                            We'll follow up within 24 hours. No sales funnel — just a
                            direct conversation about whether the audit makes sense for
                            your firm.
                        </p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label
                                htmlFor="pilot-name"
                                className="block text-xs font-medium text-slate-400 mb-1.5"
                            >
                                Your name
                            </label>
                            <input
                                id="pilot-name"
                                type="text"
                                required
                                autoComplete="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                disabled={state === "submitting"}
                                placeholder="Jane Smith"
                                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-100 placeholder-slate-600 outline-none transition focus:border-amber-500 focus:ring-1 focus:ring-amber-500 disabled:opacity-50"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="pilot-company"
                                className="block text-xs font-medium text-slate-400 mb-1.5"
                            >
                                Firm name
                            </label>
                            <input
                                id="pilot-company"
                                type="text"
                                required
                                autoComplete="organization"
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                                disabled={state === "submitting"}
                                placeholder="Acme Civil Engineering"
                                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-100 placeholder-slate-600 outline-none transition focus:border-amber-500 focus:ring-1 focus:ring-amber-500 disabled:opacity-50"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="pilot-email"
                                className="block text-xs font-medium text-slate-400 mb-1.5"
                            >
                                Work email
                            </label>
                            <input
                                id="pilot-email"
                                type="email"
                                required
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={state === "submitting"}
                                placeholder="jane@acmecivil.com"
                                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-100 placeholder-slate-600 outline-none transition focus:border-amber-500 focus:ring-1 focus:ring-amber-500 disabled:opacity-50"
                            />
                        </div>

                        {state === "error" && (
                            <p className="text-sm text-red-400">{errorMsg}</p>
                        )}

                        <button
                            type="submit"
                            disabled={state === "submitting"}
                            className="w-full mt-2 rounded-lg bg-amber-400 px-6 py-3.5 text-sm font-semibold text-slate-900 hover:bg-amber-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state === "submitting"
                                ? "Sending…"
                                : "Request an Audit →"}
                        </button>

                        <p className="text-xs text-slate-600 text-center pt-1">
                            We respond within 24 hours. No sales funnel. No free trial
                            theater.
                        </p>
                    </form>
                )}
            </div>
        </section>
    );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer() {
    return (
        <footer className="border-t border-slate-900 py-8 px-6">
            <div className="max-w-6xl mx-auto flex items-center justify-between">
                <p className="text-slate-600 text-sm">
                    © {new Date().getFullYear()} ScopeShield
                </p>
                <a
                    href={APP_LOGIN_URL}
                    className="text-sm text-slate-600 hover:text-slate-400 transition-colors"
                >
                    Sign In
                </a>
            </div>
        </footer>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function LandingPage() {
    const formRef = useRef<HTMLDivElement>(null);

    const scrollToForm = () => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="min-h-screen bg-[#020617]">
            <Nav onRequestPilot={scrollToForm} />
            <main>
                <Hero onRequestPilot={scrollToForm} />
                <Pain />
                <HowItWorks />
                <Differentiators />
                <Origin />
                <PilotForm formRef={formRef} />
            </main>
            <Footer />
        </div>
    );
}
